import { Button, Menu, MenuItem } from "@mui/material";
import "./SortButton.css";
import React, { useState, useEffect } from "react";
import SortIcon from "@mui/icons-material/Sort";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ClearIcon from "@mui/icons-material/Clear";
import { Title } from "chart.js";

function SortButton({
  setSort,
  currentSort = "",
  startDate = false,
  joinDate = false,
  name = false,
  title = false,
  createdAt = false,
  customOptions = [],
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDesc, setSortDesc] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Parse current sort state on mount and when it changes
  useEffect(() => {
    if (currentSort) {
      // Parse the sort string to extract field and direction
      const sortParams = new URLSearchParams(currentSort);
      const sortByParam = sortParams.get("sortBy");
      const sortDescParam = sortParams.get("sortDesc");

      if (sortByParam) {
        setSortBy(sortByParam);
        setSortDesc(sortDescParam === "true");
      }
    }
  }, [currentSort]);

  // Build sort options based on props
  const buildSortOptions = () => {
    const options = [];

    if (startDate) {
      options.push({ value: "startDate", label: "Start Date" });
    }
    if (joinDate) {
      options.push({ value: "joinDate", label: "Join Date" });
    }
    if (name) {
      options.push({ value: "name", label: "Name" });
    }
    if (title) {
      options.push({ value: "title", label: "Title" });
    }
    if (createdAt) {
      options.push({ value: "createdAt", label: "Created At" });
    }
    // Add custom options if provided
    if (customOptions && customOptions.length > 0) {
      options.push(...customOptions);
    }

    // Default to startDate if no options are provided
    if (options.length === 0) {
      options.push({ value: "startDate", label: "Start Date" });
    }

    return options;
  };

  const sortOptions = buildSortOptions();

  const handleSortOptionSelect = (option) => {
    setSortBy(option);
    // Apply sort immediately
    applySort(option, sortDesc);
    handleClose();
  };

  const handleDirectionChange = (isDesc) => {
    setSortDesc(isDesc);
    // Apply sort immediately with new direction
    const currentField =
      sortBy && sortBy.length
        ? sortBy
        : sortOptions.length > 0
        ? sortOptions[0].value
        : "startDate";
    applySort(currentField, isDesc);
  };

  const applySort = (field, isDesc) => {
    if (!field) {
      // Use first available option as default
      const defaultField =
        sortOptions.length > 0 ? sortOptions[0].value : "startDate";
      setSort(`&sortBy=${defaultField}`);
      return;
    }
    setSort(`&sortBy=${field}&sortDesc=${isDesc}`);
  };

  const clearSort = () => {
    setSortBy("");
    setSortDesc(false);
    setSort("");
    handleClose();
  };

  const getSortButtonText = () => {
    if (!sortBy) return "Sort By";
    const option = sortOptions.find((opt) => opt.value === sortBy);
    return option ? `${option.label}` : "Sort By";
  };

  return (
    <div className="sortButtonContainer">
      <Button
        id="sort-button"
        aria-controls={open ? "sort-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        className="sortButton"
      >
        <SortIcon className="sortIcon" />
        <span className="sortText">{getSortButtonText()}</span>
        {sortBy && (
          <span className="sortDirection">
            {sortDesc ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
          </span>
        )}
      </Button>
      <Menu
        id="sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "sort-button",
        }}
        className="sortMenu"
        PaperProps={{
          className: "sortMenuPaper",
        }}
      >
        <div className="sortMenuContent">
          <div className="sortMenuHeader">
            <h3 className="sortMenuTitle">
              <SortIcon className="sortMenuTitleIcon" />
              Sort Options
            </h3>
          </div>

          <div className="sortDirectionControls">
            <button
              className={`sortDirButton ${!sortDesc ? "active" : ""}`}
              onClick={() => handleDirectionChange(false)}
            >
              {/* <ArrowUpwardIcon /> */}
              Ascending
            </button>
            <button
              className={`sortDirButton ${sortDesc ? "active" : ""}`}
              onClick={() => handleDirectionChange(true)}
            >
              {/* <ArrowDownwardIcon /> */}
              Descending
            </button>
          </div>

          <div className="sortOptionsList">
            {sortOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                className={`sortOption ${
                  sortBy === option.value ? "active" : ""
                }`}
                onClick={() => handleSortOptionSelect(option.value)}
              >
                <div className="sortOptionContent">
                  <span className="sortOptionLabel">{option.label}</span>
                  {sortBy === option.value && (
                    <span className="sortOptionDirection">
                      {sortDesc ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    </span>
                  )}
                </div>
              </MenuItem>
            ))}
          </div>

          <div className="sortActions">
            <button className="sortClearButton" onClick={clearSort}>
              <ClearIcon />
              Clear Sort
            </button>
          </div>
        </div>
      </Menu>
    </div>
  );
}

export default SortButton;
